// stores/registerStore.ts
import { create } from "zustand";
import ENV from "../env";
import type { AuthTranslationKey } from "../services/culture/auth.translations";

enum MicrosoftState {
  Disabled,
  Enabled,
  Required,
}

interface RegisterState {
  error: { type: "raw"; message: string } | { type: "key"; key: AuthTranslationKey } | null;
  microsoftState: MicrosoftState;
  canRegister: boolean;
  setError: (error: RegisterState["error"]) => void;
  checkRegisterState: (username: string, code: string) => Promise<void>;
}

export const useRegisterStore = create<RegisterState>((set) => ({
  error: null,
  microsoftState: MicrosoftState.Disabled,
  canRegister: false,
  setError: (error) => set({ error }),
  checkRegisterState: async (username, code) => {
    // url encode the username and register code
    const encodedUsername = encodeURIComponent(username);
    const encodedRegisterCode = encodeURIComponent(code);
    try {
      const response = await fetch(`${ENV.API_SERVER}/register-state?username=${encodedUsername}&registerCode=${encodedRegisterCode}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.status === 400) {
        set({ error: { type: "raw", message: await response.text() }, canRegister: false });
        return;
      }

      if (response.status !== 200) {
        set({
          error: { type: "key", key: "errors.registerStateFailedTryAgain" },
          canRegister: false,
        });
        return;
      }

      const resJson = await response.json();
      set({
        microsoftState:
          resJson.microsoftState === "enabled"
            ? MicrosoftState.Enabled
            : resJson.microsoftState === "disabled"
              ? MicrosoftState.Disabled
              : MicrosoftState.Required,
        canRegister: true,
      });
    } catch {
      set({ error: { type: "key", key: "errors.unexpected" }, canRegister: false });
    }
  },
}));
